import { getAccessToken } from "../../utils/auth";
import React, { useEffect, useState, useCallback } from "react";
import axios from "../../utils/api";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";

function SOPDataViewer() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    employeeId: "",
    robotId: "",
    location: "",
    sopType: "",
    startDate: null,
    endDate: null,
  });
  const navigate = useNavigate();
  const limit = 2;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page,
        limit,
        ...filters,
        startDate: filters.startDate
          ? filters.startDate.toISOString()
          : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
      };

      // Remove undefined or empty values
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === "") {
          delete params[key];
        }
      });

      const response = await axios.get("/sop/employee/get", {
        params,
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });

      const result = response.data;
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error("Error fetching SOP data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date, field) => {
    setFilters((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page when filters change
    fetchData();
  };

  const resetFilters = () => {
    setFilters({
      employeeId: "",
      robotId: "",
      location: "",
      sopType: "",
      startDate: null,
      endDate: null,
    });
    setPage(1);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: "24px",
          left: "24px",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div
        style={{
          marginTop: "20px",
          maxWidth: "1400px",
          marginLeft: "auto",
          marginRight: "auto",
          padding: "24px 32px 32px",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "28px",
          }}
        >
          <h1
            style={{
              margin: 0,
              marginBottom: "4px",
              fontSize: "1.85rem",
              fontWeight: "700",
              letterSpacing: "-0.5px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Standard Operating Procedure (SOP)
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: "0.9rem",
              color: "#64748b",
              fontWeight: "400",
            }}
          >
            View and filter SOP records by employee, robot, and date
          </p>
          <span
            style={{
              display: "inline-block",
              marginTop: "8px",
              fontSize: "14px",
              color: "#64748b",
              fontWeight: "500",
            }}
          >
            {total} total records
          </span>
        </div>

        {/* Filter Form */}
        <div
          style={{
            background: "#fff",
            borderRadius: "14px",
            padding: "1.75rem 1.5rem",
            marginBottom: "24px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
            border: "1px solid #e2e8f0",
          }}
        >
          <form onSubmit={handleFilterSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "1.25rem",
                marginBottom: "1.25rem",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#64748b",
                  }}
                >
                  Employee ID
                </label>
                <input
                  type="text"
                  name="employeeId"
                  value={filters.employeeId}
                  onChange={handleFilterChange}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "0.6rem 0.75rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  placeholder="Filter by employee"
                />
              </div>

              <div style={{ minWidth: 0 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#64748b",
                  }}
                >
                  Robot ID
                </label>
                <input
                  type="text"
                  name="robotId"
                  value={filters.robotId}
                  onChange={handleFilterChange}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "0.6rem 0.75rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  placeholder="Filter by robot"
                />
              </div>

              <div style={{ minWidth: 0 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#64748b",
                  }}
                >
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "0.6rem 0.75rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  placeholder="Filter by location"
                />
              </div>

              <div style={{ minWidth: 0 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#64748b",
                  }}
                >
                  SOP Type
                </label>
                <input
                  type="text"
                  name="sopType"
                  value={filters.sopType}
                  onChange={handleFilterChange}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "0.6rem 0.75rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  placeholder="Filter by SOP type"
                />
              </div>

              <div style={{ minWidth: 0 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#64748b",
                  }}
                >
                  Start Date
                </label>
                <DatePicker
                  selected={filters.startDate}
                  onChange={(date) => handleDateChange(date, "startDate")}
                  selectsStart
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  placeholderText="Select start date"
                  className="date-picker-input"
                  wrapperClassName="date-picker-wrapper"
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: "160px", flex: "0 1 180px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#64748b",
                  }}
                >
                  End Date
                </label>
                <DatePicker
                  selected={filters.endDate}
                  onChange={(date) => handleDateChange(date, "endDate")}
                  selectsEnd
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  minDate={filters.startDate}
                  placeholderText="Select end date"
                  className="date-picker-input"
                  wrapperClassName="date-picker-wrapper"
                />
              </div>
              <button
                type="submit"
                style={{
                  padding: "0.6rem 1.25rem",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                }}
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={resetFilters}
                style={{
                  padding: "0.6rem 1.25rem",
                  background: "#f1f5f9",
                  color: "#64748b",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Reset
              </button>
            </div>
          </form>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
              background: "rgba(255,255,255,0.8)",
              borderRadius: "8px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #3a86ff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
          </div>
        )}

        {/* Table Container */}
        <div
          style={{
            borderRadius: "12px",
            border: "1px solid #eaeaea",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            marginBottom: "24px",
            background: "#fff",
          }}
        >
          <div
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "1000px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f8f9fa",
                    borderBottom: "1px solid #eaeaea",
                  }}
                >
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Employee
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Robot
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Location
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    SOP Type
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Date & Time
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Sections
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 && !isLoading ? (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        padding: "40px 20px",
                        textAlign: "center",
                        color: "#888",
                        fontSize: "14px",
                      }}
                    >
                      No SOP submissions found matching your filters
                    </td>
                  </tr>
                ) : (
                  data.map((item, i) => (
                    <>
                      <tr
                        key={`row-${i}`}
                        style={{
                          borderBottom: item.message
                            ? "none"
                            : "1px solid #f0f0f0",
                          transition: "background 0.2s ease",
                          ":hover": {
                            background: "#f9f9f9",
                          },
                        }}
                      >
                        <td
                          style={{
                            padding: "20px",
                            fontSize: "14px",
                            color: "#333",
                            fontWeight: "500",
                          }}
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                background: "#3a86ff",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "12px",
                                fontWeight: "600",
                                fontSize: "14px",
                              }}
                            >
                              {item.employeeId.charAt(0).toUpperCase()}
                            </div>
                            {item.employeeId}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 8px",
                              background: "#f0f0f0",
                              borderRadius: "4px",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            {item.robotId}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          {item.location}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          <span
                            style={{
                              color: "#3a86ff",
                              fontWeight: "500",
                            }}
                          >
                            {item.sopType}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            fontSize: "13px",
                            color: "#666",
                          }}
                        >
                          {new Date(item.date_time).toLocaleString()}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            fontSize: "13px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "16px",
                            }}
                          >
                            {Object.entries(item.sections).map(
                              ([sectionKey, section]) => (
                                <div
                                  key={sectionKey}
                                  style={{
                                    borderLeft: "3px solid #3a86ff",
                                    paddingLeft: "12px",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      marginBottom: "8px",
                                      color: "#333",
                                    }}
                                  >
                                    {section.name}
                                  </div>
                                  <ul
                                    style={{
                                      listStyle: "none",
                                      padding: 0,
                                      margin: 0,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "8px",
                                    }}
                                  >
                                    {section.taskItems.map((task, idx) => (
                                      <li
                                        key={idx}
                                        style={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          gap: "8px",
                                        }}
                                      >
                                        <span
                                          style={{
                                            color: task.completed
                                              ? "#4caf50"
                                              : "#f44336",
                                            fontSize: "16px",
                                            lineHeight: "1.4",
                                          }}
                                        >
                                          {task.completed ? "✓" : "✗"}
                                        </span>
                                        <div>
                                          <div
                                            style={{
                                              fontSize: "13px",
                                              color: "#333",
                                            }}
                                          >
                                            {task.name || "Unnamed Task"}
                                          </div>
                                          {!task.completed && task.comment && (
                                            <div
                                              style={{
                                                fontSize: "12px",
                                                color: "#ff9800",
                                                marginTop: "2px",
                                              }}
                                            >
                                              {task.comment}
                                            </div>
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                      {item.message && (
                        <tr key={`message-${i}`}>
                          <td
                            colSpan="6"
                            style={{
                              padding: "0",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "#f8fafc",
                                padding: "16px 20px",
                                fontSize: "14px",
                                color: "#333",
                                borderLeft: "4px solid #3a86ff",
                                marginLeft: "20px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "600",
                                  marginBottom: "8px",
                                }}
                              >
                                Message:
                              </div>
                              {item.message}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <button
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1 || isLoading}
            style={{
              padding: "8px 16px",
              background: page === 1 ? "#f0f0f0" : "#3a86ff",
              color: page === 1 ? "#999" : "white",
              border: "none",
              borderRadius: "6px",
              cursor: page === 1 ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
              opacity: isLoading ? 0.7 : 1,
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke={page === 1 ? "#999" : "white"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Previous
          </button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {Array.from(
              { length: Math.ceil(total / limit) || 1 },
              (_, i) => i + 1
            ).map((num) => (
              <button
                key={num}
                onClick={() => setPage(num)}
                disabled={isLoading}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  background: num === page ? "#3a86ff" : "transparent",
                  color: num === page ? "white" : "#666",
                  border: num === page ? "none" : "1px solid #e0e0e0",
                  cursor: "pointer",
                  fontWeight: num === page ? "600" : "500",
                  fontSize: "14px",
                  transition: "all 0.2s ease",
                  opacity: isLoading ? 0.7 : 1,
                  pointerEvents: isLoading ? "none" : "auto",
                }}
              >
                {num}
              </button>
            ))}
          </div>

          <button
            onClick={() => setPage((prev) => prev + 1)}
            disabled={page * limit >= total || isLoading}
            style={{
              padding: "8px 16px",
              background: page * limit >= total ? "#f0f0f0" : "#3a86ff",
              color: page * limit >= total ? "#999" : "white",
              border: "none",
              borderRadius: "6px",
              cursor: page * limit >= total ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
              opacity: isLoading ? 0.7 : 1,
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            Next
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke={page * limit >= total ? "#999" : "white"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Global styles for animations */}
        <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .date-picker-wrapper {
          width: 100%;
        }
        .date-picker-input {
          width: 100%;
          box-sizing: border-box;
          padding: 0.6rem 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          outline: none;
        }
      `}</style>
      </div>
    </div>
  );
}

export default SOPDataViewer;
